import { useState, useEffect, useRef, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CloudRain, Flame, Heart, Moon, Brain, LifeBuoy, BookOpen, Phone, PenTool, Target, Sparkles, CheckSquare, Library, Wind, ArrowLeft, ArrowRight } from 'lucide-react'
import styles from './ResourcesPage.module.css'

interface Article { icon: ReactNode; title: string; description: string; tags: string[]; }
interface Tool { icon: ReactNode; title: string; description: string; }
interface Book { icon: ReactNode; title: string; author: string; }
interface CrisisResource { label: string; number: string; }

export default function ResourcesPage() {
    const [breathingPhase, setBreathingPhase] = useState('Ready')
    const [isBreathing, setIsBreathing] = useState(false)
    const [countdown, setCountdown] = useState(4)
    const [cycleCount, setCycleCount] = useState(0)
    const [phaseIndex, setPhaseIndex] = useState(0)
    const phases = ['Inhale', 'Hold', 'Exhale', 'Hold']
    const intervalRef = useRef<number | null>(null)
    const countRef = useRef(4)
    const phaseIndexRef = useRef(0)

    useEffect(() => {
        if (isBreathing) {
            countRef.current = 4; phaseIndexRef.current = 0; setCountdown(4); setPhaseIndex(0); setBreathingPhase(phases[0])
            const tick = () => {
                setCountdown(countRef.current); setBreathingPhase(phases[phaseIndexRef.current]); setPhaseIndex(phaseIndexRef.current)
                if (countRef.current === 1) { phaseIndexRef.current = (phaseIndexRef.current + 1) % phases.length; countRef.current = 4; if (phaseIndexRef.current === 0) { setCycleCount(prev => prev + 1) } } else { countRef.current-- }
            }
            tick(); intervalRef.current = setInterval(tick, 1000)
            return () => { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null } }
        } else {
            setBreathingPhase('Ready'); setCountdown(4); setPhaseIndex(0)
            if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null }
        }
    }, [isBreathing])

    const toggleBreathing = () => { if (isBreathing) { setIsBreathing(false); setCycleCount(0) } else { setIsBreathing(true) } }

    const articles: Article[] = [
        { icon: <AlertTriangle className={styles.lucideIcon} style={{ color: '#f59e0b' }} />, title: 'Understanding Anxiety', description: 'Learn about anxiety symptoms, triggers, and effective coping strategies.', tags: ['Anxiety', 'Coping'] },
        { icon: <CloudRain className={styles.lucideIcon} style={{ color: '#6366f1' }} />, title: 'Navigating Depression', description: 'Understand the signs of depression and evidence-based approaches.', tags: ['Depression', 'Healing'] },
        { icon: <Flame className={styles.lucideIcon} style={{ color: '#ef4444' }} />, title: 'Managing Stress', description: 'Practical techniques for stress management in daily life.', tags: ['Stress', 'Mindfulness'] },
        { icon: <Heart className={styles.lucideIcon} style={{ color: '#ec4899' }} />, title: 'Healthy Relationships', description: 'Build stronger connections and learn about boundaries.', tags: ['Relationships', 'Communication'] },
        { icon: <Moon className={styles.lucideIcon} style={{ color: '#8b5cf6' }} />, title: 'Better Sleep Habits', description: 'Sleep hygiene practices to overcome insomnia naturally.', tags: ['Sleep', 'Self-Care'] },
        { icon: <Brain className={styles.lucideIcon} style={{ color: '#10b981' }} />, title: 'Cognitive Wellness', description: 'Cognitive-behavioral techniques for negative thinking.', tags: ['CBT', 'Mindset'] }
    ]

    const selfHelpTools: Tool[] = [
        { icon: <PenTool className={styles.lucideIcon} style={{ color: '#6366f1' }} />, title: 'Journaling Prompts', description: 'Guided questions to process emotions.' },
        { icon: <Target className={styles.lucideIcon} style={{ color: '#ef4444' }} />, title: 'Goal Setting', description: 'Break down goals for mental wellness.' },
        { icon: <Sparkles className={styles.lucideIcon} style={{ color: '#8b5cf6' }} />, title: 'Meditation Guides', description: 'Simple meditation practices.' },
        { icon: <CheckSquare className={styles.lucideIcon} style={{ color: '#10b981' }} />, title: 'Self-Care Checklist', description: 'Daily practices for well-being.' }
    ]

    const recommendedBooks: Book[] = [
        { icon: <BookOpen className={styles.lucideIcon} style={{ color: '#3b82f6' }} />, title: 'Feeling Good', author: 'David D. Burns' },
        { icon: <BookOpen className={styles.lucideIcon} style={{ color: '#10b981' }} />, title: 'The Anxiety and Phobia Workbook', author: 'Edmund J. Bourne' },
        { icon: <BookOpen className={styles.lucideIcon} style={{ color: '#f59e0b' }} />, title: 'The Body Keeps the Score', author: 'Bessel van der Kolk' },
        { icon: <BookOpen className={styles.lucideIcon} style={{ color: '#ef4444' }} />, title: 'Mindfulness in Plain English', author: 'Bhante Gunaratana' }
    ]

    const crisisResources: CrisisResource[] = [
        { label: 'Emergency', number: '112' },
        { label: 'iCALL', number: '1800-599-099' },
        { label: 'Vandrevala Foundation', number: '1860-2662-345' },
        { label: 'NIMHANS', number: '080-46110007' }
    ]

    return (
        <div className={styles.resourcesContainer}>
            <div className={styles.header}>
                <Link to="/dashboard" className={styles.backButton}><ArrowLeft size={16} /> Back to Dashboard</Link>
                <h1 className={styles.title}>Mental Health Resources</h1>
            </div>
            <div className={styles.content}>
                <div className={styles.crisisBanner}>
                    <div className={styles.crisisHeader}>
                        <span className={styles.crisisIcon}><LifeBuoy size={28} style={{ color: '#ef4444' }} /></span>
                        <h2 className={styles.crisisTitle}>In Crisis? Help is Available</h2>
                    </div>
                    <p className={styles.crisisDescription}>If you or someone you know is in immediate danger or experiencing a mental health crisis, please reach out to these helplines. Trained professionals are available 24/7.</p>
                    <div className={styles.hotlineGrid}>
                        {crisisResources.map((resource, index) => (
                            <a key={index} href={`tel:${resource.number.replace(/-/g, '')}`} className={styles.hotlineCard}>
                                <div className={styles.hotlineName}>{resource.label}</div>
                                <div className={styles.hotlineNumber}><Phone size={14} /> {resource.number}</div>
                            </a>
                        ))}
                    </div>
                </div>
                <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <span className={styles.sectionIcon}><Library size={24} style={{ color: '#ec4899' }} /></span>
                        <h2 className={styles.sectionTitle}>Mental Health Articles</h2>
                    </div>
                    <div className={styles.articlesGrid}>
                        {articles.map((article, index) => (
                            <div key={index} className={styles.articleCard}>
                                <div className={styles.articleIcon}>{article.icon}</div>
                                <h3 className={styles.articleTitle}>{article.title}</h3>
                                <p className={styles.articleDescription}>{article.description}</p>
                                <span className={styles.readMore}>Read More <ArrowRight size={14} /></span>
                            </div>
                        ))}
                    </div>
                </section>
                <section className={styles.breathingSection}>
                    <div className={styles.breathingContent}>
                        <div className={styles.breathingVisual}>
                            <div className={`${styles.breathingCircle} ${isBreathing ? styles[breathingPhase.toLowerCase()] : ''}`}>
                                <div className={styles.breathingInner}>
                                    <div className={styles.breathingPhaseText}>{breathingPhase}</div>
                                    {isBreathing && <div className={styles.breathingCountdown}>{countdown}</div>}
                                </div>
                            </div>
                            {isBreathing && <div className={styles.cycleCounter}>Cycles completed: {cycleCount}</div>}
                        </div>
                        <div className={styles.breathingInfo}>
                            <h2 className={styles.breathingTitle}><Wind size={24} style={{ color: '#6366f1' }} /> Box Breathing Exercise</h2>
                            <p className={styles.breathingDescription}>A powerful technique used by Navy SEALs to calm the nervous system and reduce stress. Each phase lasts 4 seconds for a complete 16-second cycle.</p>
                            <div className={styles.breathingSteps}>
                                <div className={`${styles.breathingStep} ${phaseIndex === 0 ? styles.active : ''}`}><span className={styles.stepNumber}>1</span><span className={styles.stepText}>Inhale (4s)</span></div>
                                <div className={`${styles.breathingStep} ${phaseIndex === 1 ? styles.active : ''}`}><span className={styles.stepNumber}>2</span><span className={styles.stepText}>Hold (4s)</span></div>
                                <div className={`${styles.breathingStep} ${phaseIndex === 2 ? styles.active : ''}`}><span className={styles.stepNumber}>3</span><span className={styles.stepText}>Exhale (4s)</span></div>
                                <div className={`${styles.breathingStep} ${phaseIndex === 3 ? styles.active : ''}`}><span className={styles.stepNumber}>4</span><span className={styles.stepText}>Hold (4s)</span></div>
                            </div>
                            <button className={styles.breathingButton} onClick={toggleBreathing}>{isBreathing ? 'Stop Exercise' : 'Start Breathing'}</button>
                        </div>
                    </div>
                </section>
                <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <span className={styles.sectionIcon}><PenTool size={24} style={{ color: '#6366f1' }} /></span>
                        <h2 className={styles.sectionTitle}>Self-Help Tools</h2>
                    </div>
                    <div className={styles.toolsGrid}>
                        {selfHelpTools.map((tool, index) => (
                            <div key={index} className={styles.toolCard}>
                                <div className={styles.toolIcon}>{tool.icon}</div>
                                <h3 className={styles.toolTitle}>{tool.title}</h3>
                                <p className={styles.toolDescription}>{tool.description}</p>
                            </div>
                        ))}
                    </div>
                </section>
                <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <span className={styles.sectionIcon}><BookOpen size={24} style={{ color: '#3b82f6' }} /></span>
                        <h2 className={styles.sectionTitle}>Recommended Reading</h2>
                    </div>
                    <div className={styles.booksGrid}>
                        {recommendedBooks.map((book, index) => (
                            <div key={index} className={styles.bookCard}>
                                <span className={styles.bookIcon}>{book.icon}</span>
                                <div className={styles.bookInfo}>
                                    <h4 className={styles.bookTitle}>{book.title}</h4>
                                    <p className={styles.bookAuthor}>by {book.author}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
            <div className={styles.footer}>© 2025 Shweta Heals - Your Mental Health Matters</div>
        </div>
    )
}
